import { api } from '@/lib/api'
import {
  deleteOutboxItem,
  getAllOutboxItems,
  type OutboxItem,
  putOutboxItem,
} from '@/lib/outbox'
import { queryClient } from '@/lib/query-client'

type Listener = () => void
type ApiErrorLike = {
  message?: unknown
  response?: {
    data?: {
      message?: unknown
      error?: unknown
    }
  }
}

let items: OutboxItem[] = []
const listeners = new Set<Listener>()
const previewUrls = new Map<string, string>()
let initialized = false
let flushing = false

function emit() {
  // New array reference so useSyncExternalStore detects the change.
  items = [...items]
  for (const listener of listeners) listener()
}

function setPreviewUrl(item: OutboxItem) {
  if (
    item.kind === 'media' &&
    item.fileBlob &&
    !previewUrls.has(item.clientMessageId)
  ) {
    previewUrls.set(item.clientMessageId, URL.createObjectURL(item.fileBlob))
  }
}

function clearPreviewUrl(clientMessageId: string) {
  const url = previewUrls.get(clientMessageId)
  if (url) {
    URL.revokeObjectURL(url)
    previewUrls.delete(clientMessageId)
  }
}

export function getPreviewUrl(clientMessageId: string): string | undefined {
  return previewUrls.get(clientMessageId)
}

export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getOutboxSnapshot(): OutboxItem[] {
  return items
}

export async function initOutbox(): Promise<void> {
  if (initialized) return
  initialized = true
  const stored = await getAllOutboxItems()
  // Reset any item stuck in "sending" from a previous session back to queued.
  items = stored.map((item) =>
    item.status === 'sending' ? { ...item, status: 'queued' } : item
  )
  items.forEach(setPreviewUrl)
  emit()
  void flushOutbox()
}

export async function enqueueOutbox(
  item: Omit<OutboxItem, 'status' | 'createdAt'> & { createdAt?: string }
): Promise<void> {
  const record: OutboxItem = {
    ...item,
    createdAt: item.createdAt ?? new Date().toISOString(),
    status: 'queued',
  }
  setPreviewUrl(record)
  await putOutboxItem(record)
  items = [...items, record]
  emit()
  void flushOutbox()
}

function serverTypeFor(item: OutboxItem): 'IMAGE' | 'AUDIO' | 'FILE' {
  if (item.mediaKind === 'image') return 'IMAGE'
  if (item.mediaKind === 'audio') return 'AUDIO'
  return 'FILE'
}

function messageTypeFor(item: OutboxItem): string {
  if (item.kind === 'text') return 'TEXT'
  return item.mediaKind?.toUpperCase() ?? serverTypeFor(item)
}

async function sendItem(item: OutboxItem): Promise<void> {
  const url = `/orgs/${item.orgId}/attendances/${item.conversationId}/messages`

  if (item.kind === 'media' && item.fileBlob) {
    const form = new FormData()
    form.append(
      'file',
      new File([item.fileBlob], item.fileName ?? 'arquivo', {
        type: item.fileType || item.fileBlob.type,
      })
    )
    form.append('type', serverTypeFor(item))
    if (item.content?.trim()) form.append('body', item.content.trim())
    form.append('clientMessageId', item.clientMessageId)
    await api.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } else if (item.kind === 'media' && item.mediaUrl) {
    await api.post(url, {
      body: item.content ?? undefined,
      type: messageTypeFor(item),
      mediaUrl: item.mediaUrl,
      mediaStorageKey: item.mediaStorageKey,
      mediaMimeType: item.fileType,
      mediaFileName: item.fileName,
      clientMessageId: item.clientMessageId,
    })
  } else {
    await api.post(url, {
      body: item.content ?? '',
      type: 'TEXT',
      clientMessageId: item.clientMessageId,
    })
  }
}

function isNetworkError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: unknown }).response === undefined
  )
}

function failureReasonFromError(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return 'Erro inesperado ao enviar'
  }

  const candidate = error as ApiErrorLike
  const message =
    candidate.response?.data?.message ??
    candidate.response?.data?.error ??
    candidate.message

  if (Array.isArray(message)) {
    return String(message[0] ?? 'Erro inesperado ao enviar')
  }

  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }

  return 'Erro inesperado ao enviar'
}

export async function flushOutbox(): Promise<void> {
  if (flushing) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  flushing = true

  try {
    const pending = items.filter((item) => item.status !== 'sending')
    const touchedConversations = new Set<string>()

    for (const item of pending) {
      // Mark sending.
      items = items.map((i) =>
        i.clientMessageId === item.clientMessageId
          ? { ...i, status: 'sending' }
          : i
      )
      emit()

      try {
        await sendItem(item)
        await deleteOutboxItem(item.clientMessageId)
        clearPreviewUrl(item.clientMessageId)
        items = items.filter((i) => i.clientMessageId !== item.clientMessageId)
        touchedConversations.add(item.conversationId)
        emit()
      } catch (error) {
        const status: OutboxItem['status'] = isNetworkError(error)
          ? 'queued'
          : 'failed'
        const updated = {
          ...item,
          status,
          failureReason:
            status === 'failed' ? failureReasonFromError(error) : null,
        }
        await putOutboxItem(updated)
        items = items.map((i) =>
          i.clientMessageId === item.clientMessageId ? updated : i
        )
        emit()
        // Stop the run on a network error; the online listener retries later.
        if (status === 'queued') break
      }
    }

    for (const conversationId of touchedConversations) {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'messages' &&
          query.queryKey[2] === conversationId,
      })
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
    }
  } finally {
    flushing = false
  }
}

export async function retryOutboxItem(clientMessageId: string): Promise<void> {
  const item = items.find((i) => i.clientMessageId === clientMessageId)
  if (!item) return
  const updated = { ...item, status: 'queued' as const, failureReason: null }
  await putOutboxItem(updated)
  items = items.map((i) =>
    i.clientMessageId === clientMessageId ? updated : i
  )
  emit()
  void flushOutbox()
}

export function startOutboxAutoFlush(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('online', () => void flushOutbox())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushOutbox()
  })
}

export async function removeOutboxItem(clientMessageId: string): Promise<void> {
  await deleteOutboxItem(clientMessageId)
  clearPreviewUrl(clientMessageId)
  items = items.filter((i) => i.clientMessageId !== clientMessageId)
  emit()
}
